import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Event, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { Debounce } from 'haba-contentful-components';
import { filter, switchMap, take, tap } from 'rxjs/operators';
import {
  AppUtilsService,
  BaseSiteToBrandService,
  DataLayerService,
  DlRecosService,
  ExactagService,
  JsonLDService,
} from 'haba-base-components';
import { LanguageService, StatePersistenceService, StorageSyncType, WindowRef } from 'haba-core';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LocalStorageKeys } from './@enums/storageKeys';
import { AppService } from './app.service';
import { serviceUrls } from '@app/helpers';
import {
  FragmentScrollService,
  HeaderAndFooterService,
  HeaderHeightService,
  LoaderService,
  ScrollService,
  SeoService,
} from '@app/services';
import { UserService } from './features/user/services/user.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy, AfterViewChecked {
  public currentBrand$: Observable<string>;
  public dataLayerNoScriptSafeUrl: SafeResourceUrl;
  public upButtonDisplayed: boolean = false;

  private sub: Subscription = new Subscription();
  private currentUrl: string = '';
  private lastNavigationTrigger: NavigationStart['navigationTrigger'] = 'imperative';

  constructor(
    private seoService: SeoService, // Do not remove! Ensures hreflangs are initialized on every page including homepage
    public appService: AppService,
    public loaderService: LoaderService,
    public headerAndFooterService: HeaderAndFooterService,
    private router: Router,
    private cref: ChangeDetectorRef,
    private brand: BaseSiteToBrandService,
    private dataLayerService: DataLayerService,
    private element: ElementRef,
    private winRef: WindowRef,
    private statePersistenceService: StatePersistenceService,
    private sanitizer: DomSanitizer,
    private exactagService: ExactagService,
    private jsonLdService: JsonLDService,
    private appUtilsService: AppUtilsService,
    private fragmentScrollService: FragmentScrollService,
    private oAuthService: OAuthService,
    private scrollService: ScrollService,
    private userService: UserService,
    private languageService: LanguageService,
    private headerHeightService: HeaderHeightService,
    private dlRecosService: DlRecosService
  ) {}

  ngOnInit(): void {
    this.initEnvironmentVars();
    this.initRouterEvents();
    this.cleanUpLocalStorage();
    this.toggleUpButton();
  }

  private cleanUpLocalStorage() {
    if (!this.winRef.isBrowser()) return;
    this.winRef.localStorage?.removeItem('queryID');
    const authJson = this.winRef.localStorage?.getItem('spartacus⚿⚿auth');
    if (!authJson) return;
    const auth = JSON.parse(authJson);
    if (new Date(Number.parseInt(auth?.token?.access_token_stored_at)) < new Date(1712700000000)) {
      this.oAuthService.logOut();
    }
  }

  private initEnvironmentVars() {
    this.appService.hasBanner = !this.winRef.nativeWindow?.sessionStorage.getItem('closedBanner');
    this.winRef.document.body.classList.add(environment.client);
    this.currentBrand$ = this.brand.currentBrand();
    this.dataLayerNoScriptSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.dataLayerService.getNoScriptUrl()
    );
  }

  private initRouterEvents() {
    this.headerAndFooterService.init(this.router);
    this.sub.add(
      this.router.events
        .pipe(
          tap((event) => this.handleScrollRestoration(event)),
          filter((event) => event instanceof NavigationEnd)
        )
        .subscribe(() => {
          this.appUtilsService.memoizeUrls();

          if (serviceUrls.some((url) => this.router.url.includes(url))) {
            this.jsonLdService.insertLocalBusinessSchema(this.winRef.location.href);
          }

          this.triggerExactag();

          setTimeout(() => this.fragmentScrollService.scrollToFragment());
          setTimeout(() => this.dataLayerService.setDataLayer(this.router.url));
          setTimeout(() => this.dlRecosService.resetVisitedRecos());

          this.winRef.document.body.setAttribute('data-page', this.winRef.location.pathname);

          this.cref.detectChanges();
        })
    );
  }

  ngAfterViewInit(): void {
    this.saveScreenMetadata();
    this.removeTabIndex();
    this.checkCustomerLanguage();
    this.headerHeightService.init();
  }

  checkCustomerLanguage(): void {
    combineLatest([
      this.userService.getCurrentUser().pipe(filter((user) => Boolean(user?.email))),
      this.languageService.getActive(),
    ])
      .pipe(
        take(1),
        filter(([user, language]) => user.preferredLanguage !== language),
        switchMap(([, language]) => this.userService.updatePreferredLanguage(language))
      )
      .subscribe();
  }

  ngAfterViewChecked(): void {
    this.cref.detectChanges();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  @HostListener('window:resize', ['$event'])
  @Debounce(1000)
  onResize() {
    this.saveScreenMetadata();
  }

  public scrollToTop(): void {
    if (!this.winRef.isBrowser()) {
      return;
    }

    this.winRef.nativeWindow.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private toggleUpButton(): void {
    if (!this.winRef.isBrowser()) {
      return;
    }

    let oldScrollY = window.scrollY;
    this.winRef.nativeWindow.onscroll = () => {
      if (
        this.router.url.includes('/cart') ||
        this.router.url.includes('/p/') ||
        this.router.url.includes('/checkout/')
      ) {
        this.upButtonDisplayed = false;
        return;
      }

      this.upButtonDisplayed = window.scrollY === 0 ? false : oldScrollY > window.scrollY;
      oldScrollY = window.scrollY;
    };
  }

  private saveScreenMetadata(): void {
    const containerElement = this.element.nativeElement.querySelector('.container');
    const screenElement = this.element.nativeElement.querySelector('cx-storefront');
    if (containerElement && screenElement) {
      const metadata = {
        width: {
          container: containerElement.offsetWidth,
          screen: screenElement.offsetWidth,
        },
      };

      this.statePersistenceService.syncWithStorage({
        key: LocalStorageKeys.SCREEN,
        state$: of(metadata),
        storageType: StorageSyncType.LOCAL_STORAGE,
      });
    }
  }

  private removeTabIndex(): void {
    if (!this.winRef.isBrowser()) return;
    setTimeout(() => {
      this.winRef.document.getElementsByTagName('main')[0]?.removeAttribute('tabindex');
    }, 500);
  }

  private triggerExactag(): void {
    this.exactagService.exactagInitiated$.pipe(filter(Boolean), take(1)).subscribe(() => {
      this.exactagService.runExactag(environment.exactag, this.router.url);
    });
  }

  private handleScrollRestoration(event: Event): void {
    if (!this.winRef.isBrowser()) {
      return;
    }

    if (this.scrollService.skipScroll) {
      this.scrollService.skipScroll = false;
      return;
    }

    if (event instanceof NavigationStart) {
      this.lastNavigationTrigger = event.navigationTrigger;
      // Save the current scroll position
      this.scrollService.savePosition(this.currentUrl);
    } else if (event instanceof NavigationEnd) {
      // Update the current URL
      this.currentUrl = event.urlAfterRedirects;

      if (this.router.parseUrl(this.currentUrl).fragment) return;

      // Only restore a saved scroll position on genuine browser back/forward
      // (popstate) navigation. For imperative navigations (e.g. clicking a link
      // such as the "Zurück zu ..." back link) always scroll to top.
      const savedPosition =
        this.lastNavigationTrigger === 'popstate'
          ? this.scrollService.getPosition(this.currentUrl)
          : null;
      if (savedPosition) {
        setTimeout(() => {
          window.scrollTo(savedPosition[0], savedPosition[1]);
        }, 1000);
      } else {
        window.scrollTo(0, 0); // Default scroll to top
      }
    }
  }
}
